import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, createSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// CSS Modules, react-datepicker-cssmodules.css
import "react-datepicker/dist/react-datepicker-cssmodules.css";

import { cleanObject } from "../../../helpers/clean-object";

import { Button } from "../../../components/button";

import { ScansFiltersInput } from "../../../__generated__/globalTypes";
import { ProductInput } from "../../products/components";
import { SiteInput } from "../../sites/components";
import { Search } from "lucide-react";
import { formatDate } from "../../../helpers/date";

export const SearchScanInput: React.FC<ScansFiltersInput> = (defaultValues) => {
  const navigate = useNavigate();
  const form = useForm<ScansFiltersInput>({
    defaultValues,
    mode: "all",
  });
  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  const onSearchSubmit = () => {
    const input = form.getValues();
    navigate({
      pathname: "/scans",
      search: `?${createSearchParams(cleanObject(input))}`,
    });
  };

  const [isFormOpened, setIsFormOpened] = useState(
    Object.values(defaultValues).some((v) => v)
  );

  const search = form.watch("search");
  let orderByPrice = form.watch("orderByPrice");
  let orderByQuantity = form.watch("orderByQuantity");
  let orderByPriceVariation = form.watch("orderByPriceVariation");
  let orderByQuantityVariation = form.watch("orderByQuantityVariation");
  let orderByCa = form.watch("orderByCa");
  let start = form.watch("start");
  let end = form.watch("end");

  let [startDate, setStartDate] = useState<Date | null>(null);
  let [endDate, setEndDate] = useState<Date | null>(null);

  useEffect(() => {
    if (start) {
      setStartDate(new Date(start));
    }
  }, [start]);

  useEffect(() => {
    if (end) {
      setEndDate(new Date(end));
    }
  }, [end]);

  return (
    <>
      <div className="card mb-2">
        <div className="grid gap-3 w-full items-center ">
          <div className="flex row items-center justify-between">
            <div className="flex flex-1 row align-text-center items-center">
              <label
                htmlFor="search"
                className="mr-2 text-gray-200 hover:text-gray-300"
              >
                <Search size={16} />
              </label>
              <input
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (search && search?.length > 0 && search.length < 3) {
                      toast.error(
                        "La recherche doit contenir au minimum 3 lettres"
                      );

                      return;
                    }
                    onSearchSubmit();
                  }
                }}
                placeholder="Rechercher un Produit"
                autoComplete="off"
                {...form.register("search", {
                  minLength: 3,
                  required: false,
                })}
                className="input rounded border-0 w-full text-sm"
              />
            </div>
            <span
              onClick={() => setIsFormOpened((prev) => !prev)}
              className="inline-block mr-4 cursor-pointer"
            >
              <svg
                className="text-gray-200 w-5 h-5"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.7666 7.9583L16.1916 7.4333L16.9333 5.94996C17.0085 5.7947 17.0336 5.61993 17.0053 5.44977C16.9769 5.27961 16.8964 5.12245 16.775 4.99996L15 3.22496C14.8768 3.1017 14.7182 3.02013 14.5463 2.99173C14.3743 2.96333 14.1979 2.98953 14.0416 3.06663L12.5583 3.8083L12.0333 2.2333C11.9778 2.06912 11.8726 1.92632 11.7322 1.82475C11.5918 1.72319 11.4232 1.66792 11.25 1.66663H8.74996C8.57526 1.66618 8.40483 1.72064 8.26277 1.82233C8.12071 1.92402 8.0142 2.06778 7.9583 2.2333L7.4333 3.8083L5.94996 3.06663C5.7947 2.99145 5.61993 2.9663 5.44977 2.99466C5.27961 3.02302 5.12245 3.10349 4.99996 3.22496L3.22496 4.99996C3.1017 5.1231 3.02013 5.28177 2.99173 5.45368C2.96333 5.62558 2.98953 5.80205 3.06663 5.9583L3.8083 7.44163L2.2333 7.96663C2.06912 8.02208 1.92632 8.12732 1.82475 8.26772C1.72319 8.40812 1.66792 8.57668 1.66663 8.74996V11.25C1.66618 11.4247 1.72064 11.5951 1.82233 11.7372C1.92402 11.8792 2.06778 11.9857 2.2333 12.0416L3.8083 12.5666L3.06663 14.05C2.99145 14.2052 2.9663 14.38 2.99466 14.5502C3.02302 14.7203 3.10349 14.8775 3.22496 15L4.99996 16.775C5.1231 16.8982 5.28177 16.9798 5.45368 17.0082C5.62558 17.0366 5.80205 17.0104 5.9583 16.9333L7.44163 16.1916L7.96663 17.7666C8.02253 17.9321 8.12904 18.0759 8.2711 18.1776C8.41317 18.2793 8.58359 18.3337 8.7583 18.3333H11.2583C11.433 18.3337 11.6034 18.2793 11.7455 18.1776C11.8875 18.0759 11.9941 17.9321 12.05 17.7666L12.575 16.1916L14.0583 16.9333C14.2126 17.0066 14.3856 17.0307 14.5541 17.0024C14.7225 16.9741 14.8781 16.8947 15 16.775L16.775 15C16.8982 14.8768 16.9798 14.7182 17.0082 14.5463C17.0366 14.3743 17.0104 14.1979 16.9333 14.0416L16.1916 12.5583L17.7666 12.0333C17.9308 11.9778 18.0736 11.8726 18.1752 11.7322C18.2767 11.5918 18.332 11.4232 18.3333 11.25V8.74996C18.3337 8.57526 18.2793 8.40483 18.1776 8.26277C18.0759 8.12071 17.9321 8.0142 17.7666 7.9583ZM16.6666 10.65L15.6666 10.9833C15.4367 11.0579 15.2257 11.1816 15.0483 11.3459C14.871 11.5102 14.7315 11.711 14.6395 11.9346C14.5475 12.1582 14.5053 12.3991 14.5158 12.6406C14.5262 12.8821 14.5891 13.1185 14.7 13.3333L15.175 14.2833L14.2583 15.2L13.3333 14.7C13.1196 14.5935 12.8855 14.5342 12.6469 14.526C12.4083 14.5179 12.1707 14.5611 11.9502 14.6528C11.7298 14.7445 11.5316 14.8824 11.3691 15.0573C11.2066 15.2322 11.0835 15.44 11.0083 15.6666L10.675 16.6666H9.34996L9.01663 15.6666C8.94204 15.4367 8.81832 15.2257 8.65404 15.0483C8.48977 14.871 8.28888 14.7315 8.06531 14.6395C7.84174 14.5475 7.60084 14.5053 7.35932 14.5158C7.11779 14.5262 6.88143 14.5891 6.66663 14.7L5.71663 15.175L4.79996 14.2583L5.29996 13.3333C5.41087 13.1185 5.47373 12.8821 5.48417 12.6406C5.49461 12.3991 5.45238 12.1582 5.36041 11.9346C5.26845 11.711 5.12894 11.5102 4.95158 11.3459C4.77422 11.1816 4.56325 11.0579 4.3333 10.9833L3.3333 10.65V9.34996L4.3333 9.01663C4.56325 8.94204 4.77422 8.81832 4.95158 8.65404C5.12894 8.48977 5.26845 8.28888 5.36041 8.06531C5.45238 7.84174 5.49461 7.60084 5.48417 7.35932C5.47373 7.11779 5.41087 6.88143 5.29996 6.66663L4.82496 5.74163L5.74163 4.82496L6.66663 5.29996C6.88143 5.41087 7.11779 5.47373 7.35932 5.48417C7.60084 5.49461 7.84174 5.45238 8.06531 5.36041C8.28888 5.26845 8.48977 5.12894 8.65404 4.95158C8.81832 4.77422 8.94204 4.56325 9.01663 4.3333L9.34996 3.3333H10.65L10.9833 4.3333C11.0579 4.56325 11.1816 4.77422 11.3459 4.95158C11.5102 5.12894 11.711 5.26845 11.9346 5.36041C12.1582 5.45238 12.3991 5.49461 12.6406 5.48417C12.8821 5.47373 13.1185 5.41087 13.3333 5.29996L14.2833 4.82496L15.2 5.74163L14.7 6.66663C14.5935 6.88033 14.5342 7.11442 14.526 7.35304C14.5179 7.59165 14.5611 7.82924 14.6528 8.0497C14.7445 8.27016 14.8824 8.46835 15.0573 8.63086C15.2322 8.79337 15.44 8.9164 15.6666 8.99163L16.6666 9.32496V10.65ZM9.99996 6.66663C9.34069 6.66663 8.69623 6.86213 8.14806 7.2284C7.5999 7.59467 7.17266 8.11526 6.92036 8.72435C6.66807 9.33344 6.60206 10.0037 6.73068 10.6503C6.8593 11.2969 7.17676 11.8908 7.64294 12.357C8.10911 12.8232 8.70306 13.1406 9.34966 13.2692C9.99626 13.3979 10.6665 13.3319 11.2756 13.0796C11.8847 12.8273 12.4053 12.4 12.7715 11.8519C13.1378 11.3037 13.3333 10.6592 13.3333 9.99996C13.3333 9.11591 12.9821 8.26806 12.357 7.64294C11.7319 7.01782 10.884 6.66663 9.99996 6.66663ZM9.99996 11.6666C9.67033 11.6666 9.34809 11.5689 9.07401 11.3857C8.79993 11.2026 8.58631 10.9423 8.46016 10.6378C8.33402 10.3332 8.30101 9.99811 8.36532 9.67481C8.42963 9.35151 8.58836 9.05454 8.82145 8.82145C9.05454 8.58836 9.35151 8.42963 9.67481 8.36532C9.99811 8.30101 10.3332 8.33402 10.6378 8.46016C10.9423 8.58631 11.2026 8.79993 11.3857 9.07401C11.5689 9.34809 11.6666 9.67033 11.6666 9.99996C11.6666 10.442 11.491 10.8659 11.1785 11.1785C10.8659 11.491 10.442 11.6666 9.99996 11.6666Z"
                  fill="currentColor"
                ></path>
              </svg>
            </span>
          </div>
          {isFormOpened && (
            <div className="px-4 ">
              <div className="flex flex-wrap -mx-4 -mb-4 md:mb-0">
                <div className="w-full py-1">
                  <ProductInput form={form} />
                </div>
                <div className="w-full py-1">
                  <SiteInput form={form} />
                </div>
                <div className="w-full md:w-1/3 p-1">
                  <p className="label">Prix</p>
                  <div className="relative">
                    <svg
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.3333 6.1133C11.2084 5.98913 11.0395 5.91943 10.8633 5.91943C10.6872 5.91943 10.5182 5.98913 10.3933 6.1133L8.00001 8.47329L5.64001 6.1133C5.5151 5.98913 5.34613 5.91943 5.17001 5.91943C4.99388 5.91943 4.82491 5.98913 4.70001 6.1133C4.63752 6.17527 4.58792 6.249 4.55408 6.33024C4.52023 6.41148 4.50281 6.49862 4.50281 6.58663C4.50281 6.67464 4.52023 6.76177 4.55408 6.84301C4.58792 6.92425 4.63752 6.99799 4.70001 7.05996L7.52667 9.88663C7.58865 9.94911 7.66238 9.99871 7.74362 10.0326C7.82486 10.0664 7.912 10.0838 8.00001 10.0838C8.08801 10.0838 8.17515 10.0664 8.25639 10.0326C8.33763 9.99871 8.41136 9.94911 8.47334 9.88663L11.3333 7.05996C11.3958 6.99799 11.4454 6.92425 11.4793 6.84301C11.5131 6.76177 11.5305 6.67464 11.5305 6.58663C11.5305 6.49862 11.5131 6.41148 11.4793 6.33024C11.4454 6.249 11.3958 6.17527 11.3333 6.1133Z"
                        fill="#8896AB"
                      ></path>
                    </svg>

                    <select
                      className="input appearance-none w-full"
                      {...form.register("orderByPrice")}
                    >
                      <option value={undefined} selected={!orderByPrice}>
                        -
                      </option>
                      <option value={"ASC"} selected={orderByPrice === "ASC"}>
                        Croissant
                      </option>
                      <option value={"DESC"} selected={orderByPrice === "DESC"}>
                        Décroissant
                      </option>
                    </select>
                  </div>
                </div>
                <div className="w-full md:w-1/3 p-1">
                  <p className="label">Variation de Prix</p>
                  <div className="relative">
                    <svg
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.3333 6.1133C11.2084 5.98913 11.0395 5.91943 10.8633 5.91943C10.6872 5.91943 10.5182 5.98913 10.3933 6.1133L8.00001 8.47329L5.64001 6.1133C5.5151 5.98913 5.34613 5.91943 5.17001 5.91943C4.99388 5.91943 4.82491 5.98913 4.70001 6.1133C4.63752 6.17527 4.58792 6.249 4.55408 6.33024C4.52023 6.41148 4.50281 6.49862 4.50281 6.58663C4.50281 6.67464 4.52023 6.76177 4.55408 6.84301C4.58792 6.92425 4.63752 6.99799 4.70001 7.05996L7.52667 9.88663C7.58865 9.94911 7.66238 9.99871 7.74362 10.0326C7.82486 10.0664 7.912 10.0838 8.00001 10.0838C8.08801 10.0838 8.17515 10.0664 8.25639 10.0326C8.33763 9.99871 8.41136 9.94911 8.47334 9.88663L11.3333 7.05996C11.3958 6.99799 11.4454 6.92425 11.4793 6.84301C11.5131 6.76177 11.5305 6.67464 11.5305 6.58663C11.5305 6.49862 11.5131 6.41148 11.4793 6.33024C11.4454 6.249 11.3958 6.17527 11.3333 6.1133Z"
                        fill="#8896AB"
                      ></path>
                    </svg>

                    <select
                      className="input appearance-none w-full"
                      {...form.register("orderByPriceVariation")}
                    >
                      <option
                        value={undefined}
                        selected={!orderByPriceVariation}
                      >
                        -
                      </option>
                      <option
                        value={"ASC"}
                        selected={orderByPriceVariation === "ASC"}
                      >
                        Croissant
                      </option>
                      <option
                        value={"DESC"}
                        selected={orderByPriceVariation === "DESC"}
                      >
                        Décroissant
                      </option>
                    </select>
                  </div>
                </div>
                <div className="w-full md:w-1/3 p-1">
                  <p className="label">Chiffre d'affaire</p>
                  <div className="relative">
                    <svg
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.3333 6.1133C11.2084 5.98913 11.0395 5.91943 10.8633 5.91943C10.6872 5.91943 10.5182 5.98913 10.3933 6.1133L8.00001 8.47329L5.64001 6.1133C5.5151 5.98913 5.34613 5.91943 5.17001 5.91943C4.99388 5.91943 4.82491 5.98913 4.70001 6.1133C4.63752 6.17527 4.58792 6.249 4.55408 6.33024C4.52023 6.41148 4.50281 6.49862 4.50281 6.58663C4.50281 6.67464 4.52023 6.76177 4.55408 6.84301C4.58792 6.92425 4.63752 6.99799 4.70001 7.05996L7.52667 9.88663C7.58865 9.94911 7.66238 9.99871 7.74362 10.0326C7.82486 10.0664 7.912 10.0838 8.00001 10.0838C8.08801 10.0838 8.17515 10.0664 8.25639 10.0326C8.33763 9.99871 8.41136 9.94911 8.47334 9.88663L11.3333 7.05996C11.3958 6.99799 11.4454 6.92425 11.4793 6.84301C11.5131 6.76177 11.5305 6.67464 11.5305 6.58663C11.5305 6.49862 11.5131 6.41148 11.4793 6.33024C11.4454 6.249 11.3958 6.17527 11.3333 6.1133Z"
                        fill="#8896AB"
                      ></path>
                    </svg>

                    <select
                      className="input appearance-none w-full"
                      {...form.register("orderByCa")}
                    >
                      <option value={undefined} selected={!orderByCa}>
                        -
                      </option>
                      <option value={"ASC"} selected={orderByCa === "ASC"}>
                        Croissant
                      </option>
                      <option value={"DESC"} selected={orderByCa === "DESC"}>
                        Décroissant
                      </option>
                    </select>
                  </div>
                </div>
                <div className="w-full md:w-1/2 p-1">
                  <p className="label">Quantité</p>
                  <div className="relative">
                    <svg
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.3333 6.1133C11.2084 5.98913 11.0395 5.91943 10.8633 5.91943C10.6872 5.91943 10.5182 5.98913 10.3933 6.1133L8.00001 8.47329L5.64001 6.1133C5.5151 5.98913 5.34613 5.91943 5.17001 5.91943C4.99388 5.91943 4.82491 5.98913 4.70001 6.1133C4.63752 6.17527 4.58792 6.249 4.55408 6.33024C4.52023 6.41148 4.50281 6.49862 4.50281 6.58663C4.50281 6.67464 4.52023 6.76177 4.55408 6.84301C4.58792 6.92425 4.63752 6.99799 4.70001 7.05996L7.52667 9.88663C7.58865 9.94911 7.66238 9.99871 7.74362 10.0326C7.82486 10.0664 7.912 10.0838 8.00001 10.0838C8.08801 10.0838 8.17515 10.0664 8.25639 10.0326C8.33763 9.99871 8.41136 9.94911 8.47334 9.88663L11.3333 7.05996C11.3958 6.99799 11.4454 6.92425 11.4793 6.84301C11.5131 6.76177 11.5305 6.67464 11.5305 6.58663C11.5305 6.49862 11.5131 6.41148 11.4793 6.33024C11.4454 6.249 11.3958 6.17527 11.3333 6.1133Z"
                        fill="#8896AB"
                      ></path>
                    </svg>

                    <select
                      className="input appearance-none w-full"
                      {...form.register("orderByQuantity")}
                    >
                      <option value={undefined} selected={!orderByQuantity}>
                        -
                      </option>
                      <option
                        value={"ASC"}
                        selected={orderByQuantity === "ASC"}
                      >
                        Croissant
                      </option>
                      <option
                        value={"DESC"}
                        selected={orderByQuantity === "DESC"}
                      >
                        Décroissant
                      </option>
                    </select>
                  </div>
                </div>
                <div className="w-full md:w-1/2 p-1">
                  <p className="label">Varation de Quantité</p>
                  <div className="relative">
                    <svg
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.3333 6.1133C11.2084 5.98913 11.0395 5.91943 10.8633 5.91943C10.6872 5.91943 10.5182 5.98913 10.3933 6.1133L8.00001 8.47329L5.64001 6.1133C5.5151 5.98913 5.34613 5.91943 5.17001 5.91943C4.99388 5.91943 4.82491 5.98913 4.70001 6.1133C4.63752 6.17527 4.58792 6.249 4.55408 6.33024C4.52023 6.41148 4.50281 6.49862 4.50281 6.58663C4.50281 6.67464 4.52023 6.76177 4.55408 6.84301C4.58792 6.92425 4.63752 6.99799 4.70001 7.05996L7.52667 9.88663C7.58865 9.94911 7.66238 9.99871 7.74362 10.0326C7.82486 10.0664 7.912 10.0838 8.00001 10.0838C8.08801 10.0838 8.17515 10.0664 8.25639 10.0326C8.33763 9.99871 8.41136 9.94911 8.47334 9.88663L11.3333 7.05996C11.3958 6.99799 11.4454 6.92425 11.4793 6.84301C11.5131 6.76177 11.5305 6.67464 11.5305 6.58663C11.5305 6.49862 11.5131 6.41148 11.4793 6.33024C11.4454 6.249 11.3958 6.17527 11.3333 6.1133Z"
                        fill="#8896AB"
                      ></path>
                    </svg>

                    <select
                      className="input appearance-none w-full"
                      {...form.register("orderByQuantityVariation")}
                    >
                      <option value={undefined} selected={!orderByQuantity}>
                        -
                      </option>
                      <option
                        value={"ASC"}
                        selected={orderByQuantityVariation === "ASC"}
                      >
                        Croissant
                      </option>
                      <option
                        value={"DESC"}
                        selected={orderByQuantityVariation === "DESC"}
                      >
                        Décroissant
                      </option>
                    </select>
                  </div>
                </div>
                <div className="w-full md:w-1/2 p-1">
                  <p className="label">Date Début</p>
                  <DatePicker
                    locale={"fr"}
                    selected={startDate}
                    onChange={(date) => {
                      setStartDate(date);
                      if (date) {
                        let dateStr = formatDate(date);
                        form.setValue("start", dateStr);
                      }
                    }}
                    className="input w-full"
                    wrapperClassName="w-full"
                    dateFormat="dd/MM/YYYY"
                  />
                </div>
                <div className="w-full md:w-1/2 p-1">
                  <p className="label">Date Fin</p>
                  <DatePicker
                    locale={"fr"}
                    selected={endDate}
                    isClearable // permet à l'utilisateur d'effacer la date sélectionnée
                    onChange={(date) => {
                      setEndDate(date);
                      if (date) {
                        let dateStr = formatDate(date);
                        form.setValue("end", dateStr);
                      }
                    }}
                    className="input w-full"
                    wrapperClassName="w-full"
                    dateFormat="dd/MM/YYYY"
                  />
                </div>
              </div>
              <div className="flex justify-between  mt-4">
                <div></div>
                <Button loading={false} onClick={onSearchSubmit}>
                  Rechercher
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// {...form.register("postal", {})}
//
